import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "wouter";

/**
 * Array-valued URL query state for the react-table2 column filters.
 *
 * This is a react-table2-local variant of `useQueryStateArray` from `../use-query-state`. It adds the
 * URL -> state direction: when the query params change externally (e.g. the global "clear all filters"
 * button wipes them), local state follows. The shared hook is deliberately left untouched so existing
 * consumers keep their current behaviour.
 */
export function useQueryStateArray(key: string, defaultValue: string[]): [string[], (newValue: string[]) => void] {
    const [queryArgs, setQueryArgs] = useSearchParams();

    const [value, setValueState] = useState<string[]>(queryArgs.getAll(key) || defaultValue);

    useEffect(() => {
        const paramValue = queryArgs.getAll(key);
        if (defaultValue && defaultValue.length > 0 && paramValue.length === 0) {
            setQueryArgs((prev) => {
                defaultValue.forEach((v) => {
                    prev.append(key, v);
                });
                return prev;
            });
            return;
        }
        // Keep local state in sync when the URL changes externally (e.g. a global "clear all filters").
        if (JSON.stringify(paramValue) !== JSON.stringify(value)) {
            setValueState(paramValue);
        }
    }, [defaultValue, key, queryArgs, setQueryArgs, value]);

    const setValue = useCallback(
        (newValue: string[]) => {
            setQueryArgs((prev) => {
                prev.delete(key);
                newValue.forEach((v) => {
                    prev.append(key, v);
                });
                return prev;
            });
            setValueState(newValue);
        },
        [key, setQueryArgs]
    );

    return [value, setValue];
}
